/**
 * Episode Title Generator
 *
 * Generates Narrator-style episode titles for analysis results.
 * Uses context-aware templates to create episodic framing.
 *
 * Episode Format: "Episode [N]: [Context-Specific Title]"
 *
 * Examples:
 * - Work: "Episode 3: The Email That Started It All"
 * - Dating: "Episode 7: The Text Message Standoff"
 * - Family: "Episode 12: Holiday Dinner Redux"
 * - Friends: "Episode 5: The Group Chat Chaos"
 *
 * Created: 2025-01-11 (Task 2.1: Analysis Results Integration)
 */

import type { MessageContext, DramaAnalysisResponse } from '@dramadetector/shared';

/**
 * Drama score thresholds for episode title selection
 * Used to categorize drama levels and select appropriate episode titles/recaps
 */
export const DRAMA_SCORE_THRESHOLD = {
  /** Low risk threshold (0-29: calm, non-dramatic titles) */
  LOW: 30,
  /** Medium risk threshold (30-59: moderate drama titles) */
  MEDIUM: 60,
  /** High risk threshold (60-100: dramatic, high-stakes titles) */
  HIGH: 100,
} as const;

interface EpisodeTitleTemplate {
  context: MessageContext;
  templates: string[];
}

/**
 * Context-specific episode title templates
 * Uses reality TV and episodic drama conventions
 */
const EPISODE_TITLE_TEMPLATES: EpisodeTitleTemplate[] = [
  {
    context: 'work',
    templates: [
      'The Email That Started It All',
      'Meeting Room Tension',
      'The Feedback Conversation',
      'Deadline Day Drama',
      'Slack Message Standoff',
      'Performance Review Reality',
      'Team Conflict Resolution',
      'The Budget Battle',
    ],
  },
  {
    context: 'dating',
    templates: [
      'The Text Message Standoff',
      'Date Night Expectations',
      'The Unread Message',
      'Plans vs Reality',
      'The Availability Discussion',
      'First Fight Territory',
      'Emotional Escalation',
      'The Boundary Conversation',
    ],
  },
  {
    context: 'family',
    templates: [
      'Holiday Dinner Redux',
      'The Family Group Chat',
      'Parent-Child Boundaries',
      'Sibling Dynamics',
      'Extended Family Politics',
      'The Inheritance Discussion',
      'Caregiving Responsibilities',
      'Family Expectations',
    ],
  },
  {
    context: 'friends',
    templates: [
      'The Group Chat Chaos',
      'Plans That Fell Apart',
      'Friend Zone Boundaries',
      'Social Circle Drama',
      'Weekend Plans Negotiation',
      'The Awkward Question',
      'Friend Group Dynamics',
      'The Misunderstood Intent',
    ],
  },
];

/**
 * Fallback episode titles for general/unknown contexts
 */
const GENERAL_EPISODE_TEMPLATES = [
  'The Message That Changed Things',
  'Communication Breakdown',
  'The Conversation We Needed',
  'Unspoken Expectations',
  'Pattern Recognition',
  'The Turning Point',
  'Reality Check',
  'The Moment of Truth',
];

/**
 * Generate context-aware episode title based on analysis
 *
 * Creates episodic framing for analysis results using the Narrator voice.
 * Selects titles based on drama score and message context to create
 * appropriate dramatic framing (calm → moderate → high-stakes).
 *
 * @param sessionCount - User's analysis session count (for episode number). Example: 5
 * @param context - Message context (work, dating, family, friends). Use undefined for general context
 * @param analysis - Drama analysis response (used for score-based selection)
 * @returns Formatted episode title: "Episode N: Title"
 *
 * @example
 * ```typescript
 * const analysis = { score: 75, category: 'high', ... };
 * const title = generateEpisodeTitle(5, 'work', analysis);
 * // Returns: "Episode 5: The Budget Battle" (high drama score)
 * ```
 *
 * @example
 * ```typescript
 * const analysis = { score: 15, category: 'low', ... };
 * const title = generateEpisodeTitle(1, 'dating', analysis);
 * // Returns: "Episode 1: Date Night Expectations" (low drama score)
 * ```
 */
export function generateEpisodeTitle(
  sessionCount: number,
  context: MessageContext | undefined,
  analysis: DramaAnalysisResponse
): string {
  // Get context-specific templates or fallback to general
  const contextTemplates = context
    ? EPISODE_TITLE_TEMPLATES.find((t) => t.context === context)?.templates
    : undefined;

  const templates = contextTemplates || GENERAL_EPISODE_TEMPLATES;

  // Guard clause: ensure templates array is not empty
  if (templates.length === 0) {
    return `Episode ${sessionCount}: Unexpected Error`;
  }

  // Select template based on drama score (higher scores = more dramatic titles)
  // Low risk (0-29): Use first third of templates (calmer titles)
  // Medium risk (30-59): Use middle third
  // High risk (60-100): Use last third (dramatic titles)
  const score = analysis.score ?? 0;
  const templateIndex = Math.floor((score / DRAMA_SCORE_THRESHOLD.HIGH) * templates.length);
  const clampedIndex = Math.min(Math.max(templateIndex, 0), templates.length - 1);

  const title = templates[clampedIndex];

  return `Episode ${sessionCount}: ${title}`;
}

/**
 * Generate "Previously on..." recap text based on context
 *
 * Creates episodic recap text for analysis results, mimicking TV show recaps.
 * For first episodes (previousScore undefined), returns first-time message.
 * For subsequent episodes, selects context-aware recap based on previous score.
 *
 * @param context - Message context (work, dating, family, friends). Use undefined for general context
 * @param previousScore - Previous analysis score (0-100). Use undefined for first episode
 * @returns Recap text for episode card
 *
 * @example
 * ```typescript
 * // First episode (no previous score)
 * const recap = generateEpisodeRecap('work', undefined);
 * // Returns: "Your first analysis. Let's see what patterns emerge."
 * ```
 *
 * @example
 * ```typescript
 * // Subsequent episode with previous score
 * const recap = generateEpisodeRecap('work', 45);
 * // Returns: "Last time: You navigated a delicate professional conversation."
 * ```
 *
 * @example
 * ```typescript
 * // High drama previous score
 * const recap = generateEpisodeRecap('dating', 85);
 * // Returns: "Last episode: Emotional boundaries were tested."
 * ```
 */
export function generateEpisodeRecap(
  context: MessageContext | undefined,
  previousScore?: number
): string {
  if (previousScore === undefined) {
    return 'Your first analysis. Let\'s see what patterns emerge.';
  }

  const contextRecaps: Record<MessageContext, string[]> = {
    work: [
      'Last time: You navigated a delicate professional conversation.',
      'Previously: Team dynamics were tested.',
      'Last episode: Workplace boundaries were established.',
    ],
    dating: [
      'Last time: Relationship expectations were clarified.',
      'Previously: Communication patterns emerged.',
      'Last episode: Emotional boundaries were tested.',
    ],
    family: [
      'Last time: Family dynamics shifted.',
      'Previously: Generational expectations collided.',
      'Last episode: Boundaries were challenged.',
    ],
    friends: [
      'Last time: Friend group dynamics evolved.',
      'Previously: Social expectations were negotiated.',
      'Last episode: The group chat energy shifted.',
    ],
    social: [
      'Last time: Social dynamics were navigated.',
      'Previously: Group interactions were analyzed.',
      'Last episode: Social patterns were identified.',
    ],
    other: [
      'Last time: Communication patterns emerged.',
      'Previously: Interactions were examined.',
      'Last episode: Insights were discovered.',
    ],
  };

  const recaps = context ? contextRecaps[context] : undefined;
  const defaultRecaps = [
    'Last time: Communication patterns were identified.',
    'Previously: You worked on clearer messaging.',
    'Last episode: Emotional awareness increased.',
  ];

  const options = recaps || defaultRecaps;

  // Guard clause: ensure options array is not empty
  if (options.length === 0) {
    return 'Last time: You analyzed a message.';
  }

  // Select recap based on previous score (0-29: low, 30-59: medium, 60-100: high)
  const recapIndex = Math.floor((previousScore / DRAMA_SCORE_THRESHOLD.HIGH) * options.length);
  const clampedIndex = Math.min(Math.max(recapIndex, 0), options.length - 1);

  return options[clampedIndex];
}
